import { IconDownload, IconExternalLink, IconTrash } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Typography } from "antd";
import { useMemo } from "react";
import { useGetAssetDownloadUrl } from "@/common/artemis-operations/assets";
import { confirmDeleteResource } from "@/common/confirm";
import {
	ASSET_DOWNLOAD_ERROR_DESC,
	ASSET_DOWNLOAD_ERROR_TITLE,
} from "@/common/messages";
import type { Asset } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { formatSize } from "@/common/utils";
import { CustomIcon, DateField } from "../base";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
	type RowAction,
} from "../responsive-table";

export interface AssetTableProps
	extends Omit<ResponsiveTableProps<Asset>, "columns"> {
	onDeleteAssets?: (assets: Asset[]) => void;
	onViewAsset?: (asset: Asset) => void;
}

/** Whether the asset viewer drawer can render this asset (text or image; not PDF/binary) */
function canView(asset: Asset): boolean {
	return (
		asset.mediaType.startsWith("text/") || asset.mediaType.startsWith("image/")
	);
}

function buildColumns(openAsset: (asset: Asset) => void): ColumnDef<Asset>[] {
	return [
		{
			accessorKey: "name",
			header: "Name",
			size: 250,
			cell: ({ getValue, row }) => (
				<Typography.Text
					style={{ cursor: "pointer" }}
					strong
					ellipsis={{ tooltip: getValue<string>() }}
					onClick={() => openAsset(row.original)}
				>
					{getValue<string>()}
				</Typography.Text>
			),
		},
		{
			accessorKey: "description",
			header: "Description",
			size: 200,
			cell: ({ getValue }) => (
				<Typography.Text ellipsis={{ tooltip: getValue<string>() }}>
					{getValue<string>()}
				</Typography.Text>
			),
		},
		// {
		//   accessorKey: "category",
		//   header: "Category",
		//   size: 50,
		//   cell: ({ getValue }) => (
		//     <AssetCategoryBadge category={getValue<AssetCategory>()} />
		//   ),
		// },
		{
			accessorKey: "mediaType",
			header: "Media Type",
			size: 200,
			cell: ({ getValue }) => (
				<Typography.Text>{getValue<string>()}</Typography.Text>
			),
		},
		{
			accessorKey: "size",
			header: "Size",
			size: 100,
			cell: ({ getValue }) => (
				<Typography.Text>{formatSize(getValue<number>())}</Typography.Text>
			),
		},
		{
			accessorKey: "createdAt",
			header: "Date",
			size: 130,
			enableSorting: true,
			cell: ({ row }) => <DateField value={row.original.createdAt as string} />,
		},
	];
}

/** Table for displaying multiple assets types */
export function AssetTable({
	onDeleteAssets: onDeleteAsset,
	onViewAsset,
	loading,
	...props
}: AssetTableProps) {
	const confirmDelete = (assets: Asset[]) =>
		confirmDeleteResource(() => onDeleteAsset?.(assets));

	const notify = useNotify();
	const { mutate: getDownloadUrl } = useGetAssetDownloadUrl();
	const downloadAsset = (asset: Asset) =>
		getDownloadUrl(asset.id, {
			onSuccess: (url) => window.open(url, "_blank"),
			onError: () =>
				notify.error(ASSET_DOWNLOAD_ERROR_TITLE, ASSET_DOWNLOAD_ERROR_DESC),
		});
	/** Opens the viewer when the asset can be rendered, otherwise downloads it */
	const openAsset = (asset: Asset) =>
		canView(asset) && onViewAsset ? onViewAsset(asset) : downloadAsset(asset);

	// biome-ignore lint/correctness/useExhaustiveDependencies: openAsset is recreated each render; rebuild columns only when the viewer callback changes
	const columns = useMemo(() => buildColumns(openAsset), [onViewAsset]);

	const rowActions: RowAction<Asset>[] = [
		{
			label: "Download",
			icon: <CustomIcon icon={IconDownload} />,
			onClick: downloadAsset,
		},
		{
			label: "View",
			icon: <CustomIcon icon={IconExternalLink} />,
			onClick: (asset: Asset) => onViewAsset?.(asset),
			disabled: (asset: Asset) => !canView(asset),
		},
		{
			label: "Delete",
			icon: <CustomIcon icon={IconTrash} />,
			danger: true,
			onClick: (asset: Asset) => confirmDelete([asset]),
		},
	];

	return (
		<ResponsiveTable<Asset>
			loading={loading}
			columns={columns}
			onNextPage={() => {}}
			rowActions={rowActions}
			compactRender={(asset) => (
				<article
					style={{ cursor: "pointer" }}
					onKeyDown={() => openAsset(asset)}
					onClick={() => openAsset(asset)}
				>
					<Typography.Text strong ellipsis>
						{asset.name}
					</Typography.Text>
					<br />
					<Typography.Text type="secondary">
						{asset.mediaType} · {formatSize(asset.size as number)}
					</Typography.Text>
					<br />
					<Typography.Text ellipsis={{ tooltip: asset.description }}>
						{asset.description}
					</Typography.Text>
				</article>
			)}
			{...props}
		/>
	);
}
